import store from '@/store';

export default class RemovedTagInAttendanceEvent {
  constructor(body) {
    try {
      const protocol =
        String(
          body?.protocol ??
            body?.attendance?.protocol ??
            body?.information?.protocol ??
            '',
        ) || '';
      if (!protocol) return;

      const toArray = (v) => (Array.isArray(v) ? v : []);
      const flattenItems = (arr) => {
        const first = arr?.[0];
        if (first && typeof first === 'object' && Array.isArray(first.items)) {
          return arr.flatMap((g) => g.items || []);
        }
        return arr || [];
      };
      const normalize = (t) => ({
        id: String(t?.tag_item_id ?? t?.id ?? t?.value ?? t?.tag?.id ?? ''),
        name: String(
          t?.tag_item_name ?? t?.name ?? t?.label ?? t?.tag?.name ?? '',
        ),
        color: String(
          t?.tag_item_color ?? t?.color ?? t?.hex_color ?? t?.tag?.color ?? '',
        ),
      });
      const normalizeGroup = (g) => ({
        id: String(g?.id ?? g?.tag_id ?? ''),
        name: String(g?.name ?? g?.tag_name ?? ''),
        selection: String(g?.selection ?? g?.tag_selection ?? ''),
        required: Boolean(g?.required ?? g?.tag_required ?? false),
        editable: Boolean(g?.editable ?? g?.tag_editable ?? true),
        helper_text: String(g?.helper_text ?? g?.tag_helper_text ?? ''),
        description: String(g?.description ?? g?.tag_description ?? ''),
        items: toArray(g?.items).map(normalize),
      });

      const rawTagField = body?.tag;
      const rawGroups = Array.isArray(rawTagField)
        ? rawTagField
        : rawTagField
          ? [rawTagField]
          : undefined;

      const removedTagsFromEvent =
        body?.removedTags ?? body?.removed_tags ?? body?.removedTagItems ?? [];

      const rawTags =
        rawGroups ??
        body?.tags ??
        body?.attendanceTags ??
        body?.attendance_tags ??
        body?.selectedTags ??
        body?.tagList ??
        body?.tagsSelected ??
        (body?.attendance?.tags || []);

      const tags = Array.from(
        new Map(
          flattenItems(toArray(rawTags))
            .map(normalize)
            .map((t) => [t.id, t]),
        ).values(),
      ).sort((a, b) => a.name.localeCompare(b.name));

      const normalizedRemovedTags = Array.isArray(removedTagsFromEvent)
        ? removedTagsFromEvent.map(normalize)
        : [];

      if (!tags.length) {
        const singleId =
          body?.tag_item_id ||
          body?.tagItemId ||
          body?.id ||
          (body?.tag && (body.tag.id || body.tag.tag_item_id));
        if (singleId) {
          const t = normalize({ id: singleId });
          tags.push(t);
        }
      }

      const isGroupPayload =
        Array.isArray(rawTags) &&
        rawTags.some(
          (g) => g && typeof g === 'object' && Array.isArray(g.items),
        );

      const getCurrentFlattenedFromStore = () => {
        try {
          const job =
            store.getters &&
            store.getters['jobs/getJobByProtocol'] &&
            store.getters['jobs/getJobByProtocol'](protocol);
          const groups = (job && job.attendanceTags) || [];
          const items = (groups || []).flatMap((g) => toArray(g.items));
          return Array.from(
            new Map(items.map(normalize).map((t) => [t.id, t])).values(),
          ).sort((a, b) => a.name.localeCompare(b.name));
        } catch {
          return [];
        }
      };

      const previousFlattened = getCurrentFlattenedFromStore();

      let dispatchTags = [];
      const getGroupsFromStore = () => {
        try {
          const job =
            store.getters &&
            store.getters['jobs/getJobByProtocol'] &&
            store.getters['jobs/getJobByProtocol'](protocol);
          return job && Array.isArray(job.attendanceTags)
            ? job.attendanceTags
            : [];
        } catch {
          return [];
        }
      };

      const currentGroups = getGroupsFromStore();

      if (normalizedRemovedTags.length > 0) {
        const removeIds = new Set(
          normalizedRemovedTags.map((t) => String(t.id)),
        );
        const updatedGroups = currentGroups.map((g) => {
          const items = toArray(g.items).filter(
            (it) => !removeIds.has(String(it?.id ?? it?.tag_item_id)),
          );
          return { ...g, items };
        });
        store.commit(
          'jobs/SET_ATTENDANCE_TAGS_JOB',
          { protocol, attendanceTags: updatedGroups, merge: false },
          { root: true },
        );
        dispatchTags = getCurrentFlattenedFromStore();
      } else if (isGroupPayload) {
        const incomingGroups = toArray(rawTags).map(normalizeGroup);
        const byId = new Map();

        (currentGroups || []).forEach(function (g) {
          byId.set(String(g && g.id), {
            id: String(g && g.id),
            name: String((g && g.name) || ''),
            selection: String((g && g.selection) || ''),
            required: !!(g && g.required),
            editable: !!(g && g.editable),
            helper_text: String((g && g.helper_text) || ''),
            description: String((g && g.description) || ''),
            items: toArray(g && g.items).map(normalize),
          });
        });
        incomingGroups.forEach(function (g) {
          const gid = String(g && g.id);
          const existing = byId.get(gid);
          if (!existing) return;
          const removeIds = new Set(
            toArray(g && g.items).map(function (it) {
              return String((it && it.id) || (it && it.tag_item_id) || '');
            }),
          );
          existing.items = toArray(existing.items).filter(function (it) {
            return !removeIds.has(String(it && it.id));
          });
          byId.set(gid, existing);
        });

        const updatedGroups = Array.from(byId.values());
        store.commit(
          'jobs/SET_ATTENDANCE_TAGS_JOB',
          { protocol, attendanceTags: updatedGroups, merge: false },
          { root: true },
        );
        dispatchTags = getCurrentFlattenedFromStore();
      } else {
        const keepIds = new Set(tags.map((t) => String(t.id)));
        if (currentGroups.length) {
          const updatedGroups = currentGroups.map((g) => {
            const items = toArray(g.items).filter((it) =>
              keepIds.has(String(it?.id ?? it?.tag_item_id)),
            );
            return { ...g, items };
          });
          store.commit(
            'jobs/SET_ATTENDANCE_TAGS_JOB',
            { protocol, attendanceTags: updatedGroups, merge: false },
            { root: true },
          );
        }
        dispatchTags = getCurrentFlattenedFromStore();
      }
      const dispatchIdSet = new Set(
        Array.isArray(dispatchTags)
          ? dispatchTags.map((t) => String(t && t.id))
          : [],
      );
      const removedTags = Array.isArray(previousFlattened)
        ? previousFlattened.filter((t) => !dispatchIdSet.has(String(t && t.id)))
        : [];

      window.dispatchEvent(
        new CustomEvent('attendance:tags:updated', {
          detail: { protocol, tags: dispatchTags, removedTags },
        }),
      );
    } catch (error) {}
  }
}
